import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { Store } from '@ngrx/store';
import { FormActionType } from '@rpe/registro-personas-empresas-lib';
import * as moment from 'moment';
import { emailValidator } from 'projects/registro-personas-empresas/src/app/shared/validators/email-validators';
import { Observable, Subscription } from 'rxjs';
import { TelefonosFormComponent } from '../../../../contactos/telefonos/components/telefonos-form.component';
import {
  Telefono,
  TelefonoFormInput
} from '../../../../contactos/telefonos/telefonos.model';
import { TerminoCondicionesDialogComponent } from '../../../../termino-condiciones-dialog/termino-condiciones-dialog.component';
import { DireccionGeografica } from '../../../../ubicaciones/direcciones-geograficas/direcciones-geografica.model';
import { DireccionesGeograficasFormComponent } from '../../../../ubicaciones/direcciones-geograficas/direcciones-geograficas-form/direcciones-geograficas-form.component';
import { State } from '../../../../vista-usuario/vista-usuario.state';
import { DocumentoPersonaFormComponent } from '../../../documentos-persona/documento-persona-form/documento-persona-form.component';
import { DocumentoPersonaFormInput } from '../../../documentos-persona/documentos-persona.model';
import { TipoSexoBiologico } from '../../../entity-sexo-biologico/entity-sexo-biologico.model';
import { selectAllTiposSexoBiologico } from '../../../entity-sexo-biologico/entity-sexo-biologico.selectors';
import { Sexo } from '../../../entity-sexo/entity-sexo.model';
import { User } from '../../../entity-usuarios/entity-usuarios.model';
import { Persona, PersonaFormInput } from '../../entity-personas.model';
import { selectAllSexosPublicos } from '../../../entity-sexo/entity-sexo.selectors';

@Component({
  selector: 'rpe-persona-form',
  templateUrl: './persona-form.component.html',
  styleUrls: ['./persona-form.component.scss']
})
export class PersonaFormComponent implements OnInit, OnDestroy {
  @ViewChild('datepicker') datePicker: MatDatepicker<Date>;

  maxDate = new Date();

  datePickerValue: string = '';

  @Input() set formInput(personaFormInput: PersonaFormInput) {
    this._formInput = personaFormInput;
    this.documentoPersonaFormInput = {
      action: FormActionType.View, // El documento de la persona es read only
      documento_persona: personaFormInput.documento_persona
    };
    // Si ya esta inicializado el form, actualizo los datos
    if (this.personaFormGroup) {
      this.updateForm();
    }
  }
  get formInput(): PersonaFormInput {
    return this._formInput;
  }

  @Output() formStatusChanges = new EventEmitter<string>();
  @Output() formValueChanges = new EventEmitter<Persona>();

  // Direccion geografica
  @ViewChild(DireccionesGeograficasFormComponent, { static: true })
  direccionGeograficaForm: DireccionesGeograficasFormComponent;

  direccionGeograficaFormInput: DireccionGeografica = null;
  direccionGeografica: DireccionGeografica = null;
  direccionGeograficaValid: boolean = false;

  @ViewChild(DocumentoPersonaFormComponent, { static: true })
  documentoPersonaForm: DocumentoPersonaFormComponent;
  documentoPersonaFormInput: DocumentoPersonaFormInput = null;

  @ViewChild(TelefonosFormComponent, { static: true })
  telefonosFormComponent: TelefonosFormComponent;
  formInputTelefono: TelefonoFormInput = {
    defaultPais: 'uy',
    preSelectPais: 'uy',
    telefonos: null
  };
  telefonosStatus: string;
  telefonos: Telefono[];

  showOtherField: boolean = false;

  _formInput: PersonaFormInput = null;

  personaFormGroup: FormGroup;

  usuario$: Observable<User>;
  sexosBio$: Observable<TipoSexoBiologico[]>;

  sexos: Sexo[] = [];

  subscriptionValue: Subscription;
  subscriptionStatus: Subscription;
  identidadGeneroSubscription: Subscription;

  emailFormGroup: FormGroup = this.fb.group({
    direccion: ['', [Validators.required, emailValidator]],
    tipo_correo_id: [1, []]
  });

  constructor(
    private fb: FormBuilder,
    public store: Store<State>,
    private dialog: MatDialog
  ) {}

  dateValidator(control: FormGroup): { [s: string]: boolean } {
    if (control.value) {
      const date = new Date(control.value);
      const today = new Date();
      if (date > today) {
        return { invalidDate: true };
      }
    }
    return null;
  }

  ngOnInit(): void {
    this.sexosBio$ = this.store.select(selectAllTiposSexoBiologico);
    this.identidadGeneroSubscription = this.store
      .select(selectAllSexosPublicos)
      .subscribe((ss) => (this.sexos = ss));
    // Creacion de nueva persona
    if (this.formInput.action == FormActionType.Add) {
      this.personaFormGroup = this.fb.group({
        nombre_completo: ['', [Validators.required]],
        primer_nombre: ['', []],
        segundo_nombre: ['', []],
        primer_apellido: ['', []],
        segundo_apellido: ['', []],
        identidad_genero_id: ['', [Validators.required]],
        identidad_genero_otro: ['', []],
        sexo_biologico_id: [null, [Validators.required]],
        documentos_persona: this.fb.array([
          this.documentoPersonaForm.createGroup()
        ]),
        fecha_nacimiento: ['', [Validators.required]],
        correo_electronico: this.emailFormGroup,
        acepta_terminos: ['', [Validators.requiredTrue]],
        actualizado: [true, []]
      });
    } else {
      // Modificacion de persona esta implementado en otro componente
    }

    this.updateForm();

    // emit events
    this.subscriptionValue = this.personaFormGroup.valueChanges.subscribe(
      (_) => {
        this.updateValues();
      }
    );

    this.subscriptionStatus = this.personaFormGroup.statusChanges.subscribe(
      (f) => {
        this.updateStatus();
      }
    );
  }

  updateValues() {
    this.formValueChanges.emit({
      ...this.personaFormGroup.value,
      telefonos: this.telefonos,
      direccion_geografica: this.direccionGeografica
    });
  }

  onSexoBioChange($event: MatRadioChange) {
    this.personaFormGroup.patchValue({
      sexo_biologico_id: $event.value
    });
  }

  ngOnDestroy(): void {
    this.subscriptionValue.unsubscribe();
    this.subscriptionStatus.unsubscribe();
    this.identidadGeneroSubscription.unsubscribe();
  }

  updateStatus() {
    const status =
      this.personaFormGroup.status == 'VALID' &&
      this.direccionGeograficaValid &&
      this.telefonosStatus == 'VALID'
        ? 'VALID'
        : 'INVALID';
    this.formStatusChanges.emit(status);
  }

  reset(): void {}

  onShowErrors(): void {
    this.personaFormGroup.markAllAsTouched();
    this.direccionGeograficaForm.checkErrors();
    this.telefonosFormComponent.checkErrors();
  }

  // Direccion Geografica
  direccionGeograficaChange(dg: any) {
    this.direccionGeografica = dg;
  }

  direccionGeograficaStatusChange(s: any) {
    this.direccionGeograficaValid = s === 'VALID';
    this.updateStatus();
  }

  onSexoChange($event: MatRadioChange) {
    if ($event.value === 0) {
      this.personaFormGroup
        .get('identidad_genero_otro')
        .setValidators([Validators.required]);
      this.personaFormGroup
        .get('identidad_genero_otro')
        .updateValueAndValidity();
      this.personaFormGroup.get('identidad_genero_id').clearValidators();
      this.showOtherField = true;
    } else {
      this.personaFormGroup.get('identidad_genero_otro').clearValidators();
      this.personaFormGroup.get('identidad_genero_otro').setValue(null);
      this.personaFormGroup
        .get('identidad_genero_otro')
        .updateValueAndValidity();
      this.personaFormGroup
        .get('identidad_genero_id')
        .setValidators([Validators.required]);
      this.showOtherField = false;
    }

    this.personaFormGroup.patchValue({
      identidad_genero_id: $event.value === 0 ? null : $event.value
    });
  }

  updateForm() {
    this.documentoPersonaForm.setValorDocumento(
      this.formInput.documento_persona
    );
    this.documentoPersonaForm.setSoloLectura(true);
    if (this.personaFormGroup && this._formInput.user) {
      this.personaFormGroup.patchValue({
        nombre_completo: this._formInput.user.nombre_completo,
        primer_nombre: this._formInput.user.primer_nombre,
        segundo_nombre: this._formInput.user.segundo_nombre,
        primer_apellido: this._formInput.user.primer_apellido,
        segundo_apellido: this._formInput.user.segundo_apellido,
        correo_electronico: this.emailFormGroup.patchValue({
          direccion: this._formInput.user.email,
          tipo_correo_id: [1, []]
        })
      });
    }
  }

  validateDate(date: string): boolean {
    const fechaRegex = /^\d{2}\/\d{2}\/\d{4}$/;

    if (!fechaRegex.test(date)) {
      this.personaFormGroup.get('fecha_nacimiento').setErrors({ format: true });
      return false;
    }

    const partes = date.split('/');

    const dia = parseInt(partes[0], 10);
    const mes = parseInt(partes[1], 10);
    const anio = parseInt(partes[2], 10);

    if (dia < 1 || dia > 31 || mes < 1 || mes > 12 || anio < 0) {
      return false;
    }

    const fecha = new Date(anio, mes - 1, dia);

    return (
      fecha.getDate() === dia &&
      fecha.getMonth() === mes - 1 &&
      fecha.getFullYear() === anio
    );
  }

  onDateChange($event: any): void {
    if (this.datePicker.close) {
      this.datePicker.close();
    }

    this.datePickerValue = $event.target.value;

    if (this.isMaxDate($event.target.value)) {
      this.personaFormGroup
        .get('fecha_nacimiento')
        .setErrors({ maxDate: true });
      return;
    }

    if (!$event.target.value) {
      this.personaFormGroup
        .get('fecha_nacimiento')
        .setErrors({ required: true });
      return;
    }

    if (this.validateDate($event.target.value)) {
      this.personaFormGroup.get('fecha_nacimiento').clearValidators();
    } else {
      this.personaFormGroup.get('fecha_nacimiento').setErrors({ format: true });
    }
  }

  dateChange($event: any) {
    if (this.datePicker.close) {
      this.datePicker.close();
    }

    if (this.isMaxDate(moment($event.target.value).format('DD/MM/YYYY'))) {
      this.personaFormGroup
        .get('fecha_nacimiento')
        .setErrors({ maxDate: true });
      return;
    }

    if (!$event.target.value) {
      this.personaFormGroup
        .get('fecha_nacimiento')
        .setErrors({ required: true });
      return;
    }

    if (this.validateDate(moment($event.target.value).format('DD/MM/YYYY'))) {
      this.personaFormGroup.get('fecha_nacimiento').clearValidators();
    } else {
      this.personaFormGroup.get('fecha_nacimiento').setErrors({ format: true });
    }

    this.personaFormGroup
      .get('fecha_nacimiento')
      .setValue(moment($event.target.value).format('DD/MM/YYYY'));
  }

  isMaxDate(date: string): boolean {
    const fechaParts = date.split('/');
    const dia = parseInt(fechaParts[0], 10);
    const mes = parseInt(fechaParts[1], 10) - 1;
    const anio = parseInt(fechaParts[2], 10);
    const fechaIngresada = new Date(anio, mes, dia);

    const fechaActual = this.maxDate;

    return fechaIngresada > fechaActual;
  }

  openTerminoCondiciones(): void {
    this.dialog.open(TerminoCondicionesDialogComponent, {
      width: '650px'
    });
  }

  //Telefonos
  telefonoFormValueChange(telefonos: any) {
    this.telefonos = telefonos;
    this.updateValues();
  }

  telefonoFormStatusChange(s: any) {
    this.telefonosStatus = s;
    this.updateStatus();
  }
}
